/**
 * !IMPORTANT
 * THIS FILE IS AUTOGENERATED, DO NOT MAKE CHANGES DIRECTLY IN THIS FILE
 */

import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

/**
 * ZOD SCHEMAS
 */
const Errors = z.enum([
  'AlreadyExists',
  'CancelledRequest',
  'CouldNotCreate',
  'CouldNotDelete',
  'CouldNotUpdate',
  'BusinessRule',
  'Forbidden',
  'BadRequest',
  'SystemError',
  'ParseError',
  'NoValueError',
  'Undefined',
  'Validation',
  'Database',
  'Exception',
  'NotFound',
  'CreateFailed',
  'UpdateFailed',
  'DeleteFailed',
  'Conflict',
  'UnauthorizedAccess',
  'ForbiddenOperation',
  'Timeout',
  'ServiceUnavailable',
  'DependencyFailed',
  'InvalidState',
]);
const Error = z
  .object({ reason: Errors, code: z.string(), message: z.string() })
  .strict();
const ActionStatuses = z.enum([
  'New',
  'Planned',
  'Active',
  'Completed',
  'Closed',
  'Monitor',
]);
const ActionPriorities = z.enum(['None', 'Low', 'Medium', 'High']);
const CommentResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    entityId: z.string().uuid(),
    text: z.string(),
  })
  .strict();
const ActionResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    improvementAreaId: z.string().uuid(),
    title: z.string(),
    problemStatement: z.string(),
    solutionProposal: z.string(),
    definitionOfDone: z.string(),
    status: ActionStatuses,
    responsibleEmployee: z.string().nullable(),
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    priority: ActionPriorities,
    comments: z.array(CommentResponse),
    suggestionIds: z.array(z.string().uuid()),
  })
  .strict();
const ActionResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(ActionResponse).nullable(),
  })
  .strict();
const ApiResult = z
  .object({ hasSucceeded: z.boolean(), errors: z.array(Error).nullable() })
  .strict();
const CreateActionRequest = z
  .object({
    improvementAreaId: z.string().uuid(),
    title: z.string(),
    problemStatement: z.string(),
    solutionProposal: z.string(),
    definitionOfDone: z.string(),
    status: ActionStatuses,
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    priority: ActionPriorities,
    responsibleEmployee: z.string().nullable(),
    suggestionIds: z.array(z.string().uuid()).nullable(),
  })
  .strict();
const ActionResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ActionResponse,
  })
  .strict();
const UpdateActionRequest = z
  .object({
    improvementAreaId: z.string().uuid(),
    title: z.string(),
    problemStatement: z.string(),
    solutionProposal: z.string(),
    definitionOfDone: z.string(),
    status: ActionStatuses,
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    priority: ActionPriorities,
    responsibleEmployee: z.string().nullable(),
    suggestionIds: z.array(z.string().uuid()),
  })
  .strict();
const CreateCommentRequest = z.object({ text: z.string() }).strict();
const CommentResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: CommentResponse,
  })
  .strict();
const ActivityTypeResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    activityTypeCode: z.string().nullable(),
    activityIds: z.array(z.string().uuid()),
  })
  .strict();
const ProblemDetails = z
  .object({
    type: z.string().nullable(),
    title: z.string().nullable(),
    status: z.number().int().nullable(),
    detail: z.string().nullable(),
    instance: z.string().nullable(),
  })
  .strict()
  .passthrough();
const CreateActivityTypeRequest = z
  .object({
    name: z.string(),
    description: z.string().nullable(),
    activityTypeCode: z.string().nullable(),
    separateInvoice: z.boolean().nullable(),
  })
  .strict();
const ActivityResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    activityTypeId: z.string().uuid(),
    zoneId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    activityCode: z.string(),
    separateInvoice: z.boolean(),
    subZoneIds: z.array(z.string().uuid()),
  })
  .strict();
const ActivityResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ActivityResponse,
  })
  .strict();
const UpdateActivityTypeRequest = z
  .object({
    name: z.string(),
    description: z.string().nullable(),
    activityTypeCode: z.string().nullable(),
    separateInvoice: z.boolean().nullable(),
    isActive: z.boolean(),
  })
  .strict();
const ActivityTypeResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ActivityTypeResponse,
  })
  .strict();
const CreateActivityRequest = z
  .object({
    activityTypeId: z.string().uuid(),
    zoneId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    activityCode: z.string(),
    separateInvoice: z.boolean().nullable(),
  })
  .strict();
const ConnectActivityRequest = z
  .object({
    activityId: z.string().uuid(),
    zoneId: z.string().uuid(),
    subZoneId: z.string().uuid().nullable(),
  })
  .strict();
const BackfillFileImportSummariesRequest = z
  .object({
    year: z.number().int(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
  })
  .strict();
const BackfillFileImportSummariesResult = z
  .object({ rebuiltCount: z.number().int(), failedCount: z.number().int() })
  .strict();
const BackfillFileImportSummariesResultApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: BackfillFileImportSummariesResult,
  })
  .strict();
const ReprocessCombinedImportDataRequest = z
  .object({
    year: z.number().int(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
  })
  .strict();
const ReprocessCombinedImportDataResult = z
  .object({
    resetRowCount: z.number().int(),
    combinedFileTypesProcessed: z.number().int(),
    totalCreated: z.number().int(),
  })
  .strict();
const ReprocessCombinedImportDataResultApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ReprocessCombinedImportDataResult,
  })
  .strict();
const SynchronizeAggregatedInternalsRequest = z
  .object({
    tenantId: z.string().uuid(),
    year: z.number().int(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
  })
  .strict();
const SynchronizemovingTotalInternalsRequest = z
  .object({
    tenantId: z.string().uuid(),
    year: z.number().int(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
  })
  .strict();
const SynchronizeProducedActivitiesRequest = z
  .object({
    tenantId: z.string().uuid(),
    year: z.number().int(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
  })
  .strict();
const SkippedRecordsDetailsResponse = z
  .object({
    errorRecordsCount: z.number().int(),
    noMatchRecordsCount: z.number().int(),
  })
  .strict();
const FileImportSummaryResponse = z.array(
  z
    .object({
      fileId: z.string().uuid(),
      fileName: z.string(),
      friendlyFileName: z.string(),
      isProcessed: z.boolean(),
      totalRecordsCount: z.number().int(),
      processedRecordsCount: z.number().int(),
      skippedRecordsCount: z.number().int(),
      skippedRecordsDetails: SkippedRecordsDetailsResponse,
      importedAt: z.string().datetime({ offset: true }),
    })
    .strict(),
);
const FileImportSummaryResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: FileImportSummaryResponse,
  })
  .strict();
const File_UploadSuggestionImage_Body = z
  .object({ File: z.instanceof(File), FileName: z.string() })
  .strict()
  .passthrough();
const StringApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.string().nullable(),
  })
  .strict();
const File_UploadFile_Body = z
  .object({ File: z.instanceof(File), FriendlyFileName: z.string() })
  .strict()
  .passthrough();
const FileResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    fileType: z.string(),
    fileHash: z.string(),
    originalFileName: z.string(),
    fileName: z.string(),
    fileExtension: z.string(),
    friendlyFileName: z.string(),
    fileUrl: z.string().nullable(),
    isProcessed: z.boolean(),
    processedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
const FileResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: FileResponse,
  })
  .strict();
const ImprovementAreaResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    branchId: z.string().uuid().nullable(),
    distributionCenterId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .strict();
const ImprovementAreaResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(ImprovementAreaResponse).nullable(),
  })
  .strict();
const CreateImprovementAreaRequest = z
  .object({
    branchId: z.string().uuid().nullable(),
    distributionCenterId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .strict();
const ImprovementAreaResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ImprovementAreaResponse,
  })
  .strict();
const UpdateImprovementAreaRequest = z
  .object({
    branchId: z.string().uuid().nullable(),
    distributionCenterId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .strict();
const FlexHRMEmployeesRequest = z
  .object({
    branchId: z.string().uuid(),
    distributionCenterId: z.string().uuid(),
    flexCompanyNumber: z.string(),
  })
  .strict();
const EmployeeId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const ActorId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const TenantId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const BranchId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const DistributionCenterId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const EmployeeNumberId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const EmployeeNumber = z
  .object({
    id: EmployeeNumberId,
    isActive: z.boolean(),
    isDeleted: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: ActorId,
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: ActorId,
    tenantId: TenantId,
    connectedSystemId: z.string().uuid(),
    employeeId: EmployeeId,
    externalEmployeeId: z.string().nullable(),
    employeeNumberValue: z.string(),
  })
  .strict();
const Employee = z
  .object({
    id: EmployeeId,
    isActive: z.boolean(),
    isDeleted: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: ActorId,
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: ActorId,
    tenantId: TenantId,
    branchId: BranchId,
    distributionCenterId: DistributionCenterId,
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    mobile: z.string().nullable(),
    employeeNumbers: z.array(EmployeeNumber),
  })
  .strict();
const UpdateFlexEmployeesResponse = z
  .object({
    createdEmployees: z.array(Employee),
    updatedEmployees: z.array(Employee),
    failedEmployeeInsertions: z.array(z.string()),
    createdEmployeeNumbers: z.array(EmployeeNumber),
    updatedEmployeeNumbers: z.array(EmployeeNumber),
  })
  .strict();
const UpdateFlexEmployeesResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: UpdateFlexEmployeesResponse,
  })
  .strict();
const FlexHRMDateRequest = z
  .object({
    flexCompanyNumber: z.string(),
    warehouseAccountCode: z.string(),
    year: z.number().int(),
    month: z.number().int(),
    day: z.number().int(),
  })
  .strict();
const ActivityId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const ProducedActivityResult = z
  .object({
    activityId: ActivityId,
    employeeId: EmployeeId,
    spentTimeInMinutes: z.number().int(),
  })
  .strict();
const RefreshDateResponse = z
  .object({
    producedActivitiesUpdated: z.array(ProducedActivityResult),
    producedActivitiesAdded: z.array(ProducedActivityResult),
    failedInsertions: z.array(ProducedActivityResult),
    producedActivitiesRemoved: z.array(ProducedActivityResult),
  })
  .strict();
const RefreshDateResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: RefreshDateResponse,
  })
  .strict();
const InternalResolution = z.enum(['Zone', 'SubZone', 'Employee']);
const AggregatedInternalBucketResolution = z.enum([
  'Day',
  'Hour',
  'QuarterHour',
]);
const DateResolution = z
  .object({
    year: z.number().int().nullable(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
  })
  .strict();
const CalculatedInternalRequest = z
  .object({
    resolution: InternalResolution,
    bucketResolution: AggregatedInternalBucketResolution,
    unitOfMeasureId: z.string().uuid().nullable(),
    returnRawData: z.boolean().nullable(),
    dates: z.array(DateResolution).nullable(),
  })
  .strict();
const CalculatedInternalResponse = z
  .object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    zoneId: z.string().uuid(),
    subZoneId: z.string().uuid().nullable(),
    employeeId: z.string().uuid().nullable(),
    bucketResolution: AggregatedInternalBucketResolution,
    year: z.number().int().nullable(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
    hour: z.number().int().nullable(),
    minute: z.number().int().nullable(),
    receivedItemCount: z.number().nullable(),
    processedItemCount: z.number().nullable(),
    backlogItemCount: z.number().nullable(),
    unitOfMeasureId: z.string().uuid(),
  })
  .strict();
const CalculatedInternalResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(CalculatedInternalResponse).nullable(),
  })
  .strict();
const ProcessAggregatedInternalsRequest = z
  .object({
    bucketResolution: AggregatedInternalBucketResolution,
    dates: z.array(DateResolution).nullable(),
  })
  .strict();
const AggregatedInternalId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const INotification = z.object({}).strict();
const ZoneId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const SubZoneId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const UnitOfMeasureId = z
  .object({ value: z.string().uuid(), isEmpty: z.boolean() })
  .strict();
const AggregatedInternal = z
  .object({
    id: AggregatedInternalId,
    isActive: z.boolean(),
    isDeleted: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: ActorId,
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: ActorId,
    domainEvents: z.array(INotification),
    tenantId: TenantId,
    zoneId: ZoneId,
    subZoneId: SubZoneId,
    employeeId: EmployeeId,
    bucketResolution: AggregatedInternalBucketResolution,
    date: z.string().datetime({ offset: true }),
    year: z.number().int(),
    monthNumber: z.number().int(),
    weekNumber: z.number().int(),
    dayOfMonth: z.number().int(),
    dayOfWeek: z.number().int(),
    hour: z.number().int(),
    minute: z.number().int(),
    receivedItemCount: z.number().nullable(),
    processedItemCount: z.number().nullable(),
    backlogItemCount: z.number().nullable(),
    unitOfMeasureId: UnitOfMeasureId,
  })
  .strict();
const ProcessAggregatedInternalResult = z
  .object({
    created: z.array(AggregatedInternal),
    updated: z.array(AggregatedInternal),
    errors: z.array(Error),
  })
  .strict();
const ProcessAggregatedInternalResultApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: ProcessAggregatedInternalResult,
  })
  .strict();
const CreateAggregatedInternalRequest = z
  .object({
    zoneId: z.string().uuid(),
    subZoneId: z.string().uuid().nullable(),
    employeeId: z.string().uuid().nullable(),
    bucketResolution: AggregatedInternalBucketResolution,
    year: z.number().int().nullable(),
    monthNumber: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
    hour: z.number().int().nullable(),
    minute: z.number().int().nullable(),
    receivedItemCount: z.number().nullable(),
    processedItemCount: z.number().nullable(),
    backlogItemCount: z.number().nullable(),
    unitOfMeasureId: z.string().uuid(),
  })
  .strict();
const IssueMobileTokenRequest = z
  .object({ attestationToken: z.string().min(1), tenantId: z.string().uuid() })
  .strict();
const MobileTokenBrokerResponse = z
  .object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    tokenType: z.string(),
    scope: z.string(),
  })
  .strict();
const MobileTokenBrokerResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: MobileTokenBrokerResponse,
  })
  .strict();
const BaseRequest = z
  .object({
    zoneId: z.string().uuid().nullable(),
    subZoneId: z.string().uuid().nullable(),
    dates: z.array(DateResolution).nullable(),
  })
  .strict();
const PredictionResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    zoneId: z.string().uuid().nullable(),
    date: z.string().datetime({ offset: true }),
    year: z.number().int(),
    monthNumber: z.number().int(),
    weekNumber: z.number().int(),
    dayOfMonth: z.number().int(),
    dayOfWeek: z.number().int(),
    value: z.number(),
    unitOfMeasureId: z.string().uuid().nullable(),
    PredictionConfidence: z.number().nullable(),
    isCreatedByUser: z.boolean().nullable(),
  })
  .strict();
const PredictionResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(PredictionResponse).nullable(),
  })
  .strict();
const CreatePredictionRequest = z
  .object({
    zoneId: z.string().uuid(),
    year: z.number().int(),
    monthNumber: z.number().int(),
    dayOfMonth: z.number().int(),
    value: z.number(),
    unitOfMeasureId: z.string().uuid(),
  })
  .strict();
const PredictionResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: PredictionResponse,
  })
  .strict();
const ProducedActivityRequest = z
  .object({
    activityTypeId: z.string().uuid().nullable(),
    activityId: z.string().uuid().nullable(),
    employeeId: z.string().uuid().nullable(),
    dates: z.array(DateResolution).nullable(),
  })
  .strict();
const ProducedActivityResponse = z
  .object({
    id: z.string().uuid(),
    tenantId: z.string().uuid().nullable(),
    date: z.string().datetime({ offset: true }).nullable(),
    year: z.number().int().nullable(),
    monthNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
    spentTime: z.number(),
    activityTypeId: z.string().uuid(),
    activityId: z.string().uuid(),
    employeeId: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid().nullable(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
  })
  .strict();
const ProducedActivityResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(ProducedActivityResponse).nullable(),
  })
  .strict();
const AggregatedActivityResolution = z.enum([
  'ActivityType',
  'Activity',
  'Employee',
]);
const AggregatedActivityRequest = z
  .object({
    resolution: AggregatedActivityResolution,
    dates: z.array(DateResolution).nullable(),
  })
  .strict();
const AggregatedActivityResponse = z
  .object({
    activityTypeId: z.string().uuid().nullable(),
    activityId: z.string().uuid().nullable(),
    employeeId: z.string().uuid().nullable(),
    year: z.number().int().nullable(),
    monthNumber: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    weekNumber: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
    spentTime: z.number(),
  })
  .strict();
const AggregatedActivityResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(AggregatedActivityResponse).nullable(),
  })
  .strict();
const SuggestionResponse = z
  .object({
    id: z.string().uuid().optional(),
    tenantId: z.string().uuid().optional(),
    improvementAreaId: z.string().uuid().optional(),
    title: z.string(),
    problemStatement: z.string(),
    solutionProposal: z.string().nullish(),
    status: z.string(),
    createdBy: z.string().nullish(),
    isActive: z.boolean().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).nullish(),
    updatedBy: z.string().uuid().nullish(),
    sasUris: z.array(z.string()).optional(),
  })
  .strict();
const SuggestionResponseIEnumerableApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: z.array(SuggestionResponse).nullable(),
  })
  .strict();
const CreateSuggestionRequest = z
  .object({
    improvementAreaId: z.string().uuid().optional(),
    title: z.string(),
    problemStatement: z.string(),
    solutionProposal: z.string().nullish(),
    createdBy: z.string().nullish(),
  })
  .strict();
const SuggestionResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: SuggestionResponse,
  })
  .strict();
const BoundaryResponse = z
  .object({
    minProducedActivityDate: z.string().datetime({ offset: true }).nullable(),
    maxProducedActivityDate: z.string().datetime({ offset: true }).nullable(),
    producedActivitiesUpdated: z.string().datetime({ offset: true }).nullable(),
    minProducedPickingRowDate: z.string().datetime({ offset: true }).nullable(),
    maxProducedPickingRowDate: z.string().datetime({ offset: true }).nullable(),
    producedPickingRowsUpdated: z
      .string()
      .datetime({ offset: true })
      .nullable(),
  })
  .strict();
const BranchResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    tenantId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    distributionCenterIds: z.array(z.string().uuid()),
  })
  .strict();
const CountryResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    name: z.string(),
    countryCode: z.string(),
  })
  .strict();
const CityResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    name: z.string(),
    country: CountryResponse,
  })
  .strict();
const AddressResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    addressTypeId: z.string().uuid(),
    street: z.string(),
    co: z.string().nullable(),
    zip: z.string().nullable(),
    city: CityResponse,
    country: CountryResponse,
  })
  .strict();
const DistributionCenterResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    branchId: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    areaIds: z.array(z.string().uuid()).nullable(),
    zoneIds: z.array(z.string().uuid()).nullable(),
    addresses: z.array(AddressResponse).nullable(),
  })
  .strict();
const AreaResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    distributionCenterId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    zoneIds: z.array(z.string().uuid()),
  })
  .strict();
const ProductivityGoalResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    zoneId: z.string().uuid(),
    subZoneId: z.string().uuid().nullable(),
    productivityNumber: z.number(),
    validFrom: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
const ProductivityNumberResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    zoneId: z.string().uuid(),
    value: z.number().int(),
  })
  .strict();
const PercentOfTotalRowsResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    zoneId: z.string().uuid(),
    value: z.number().nullable(),
  })
  .strict();
const ZoneResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    subZoneIds: z.array(z.string().uuid()).nullable(),
    productivityGoals: z.array(ProductivityGoalResponse).nullable(),
    productivityNumbers: z.array(ProductivityNumberResponse).nullable(),
    percentOfTotalRows: z.array(PercentOfTotalRowsResponse).nullable(),
  })
  .strict();
const SubZoneResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    zoneId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    subZoneCode: z.string(),
    IsVirtual: z.boolean(),
    productivityGoals: z.array(ProductivityGoalResponse).nullable(),
  })
  .strict();
const EmployeeNumberResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    connectedSystemId: z.string().uuid(),
    employeeId: z.string().uuid(),
    employeeNumber: z.string(),
  })
  .strict();
const EmployeeResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    mobile: z.string().nullable(),
    employeeNumbers: z.array(EmployeeNumberResponse),
  })
  .strict();
const UnitOfMeasureResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    TenantId: z.string().uuid().nullable(),
    name: z.string(),
    abbreviation: z.string(),
    description: z.string().nullable(),
  })
  .strict();
const TenantResponse = z
  .object({
    id: z.string().uuid(),
    isActive: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }).nullable(),
    updatedBy: z.string().uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    boundaries: BoundaryResponse,
    branches: z.array(BranchResponse),
    distributionCenters: z.array(DistributionCenterResponse),
    areas: z.array(AreaResponse),
    zones: z.array(ZoneResponse),
    subZones: z.array(SubZoneResponse),
    employees: z.array(EmployeeResponse),
    activityTypes: z.array(ActivityTypeResponse),
    activities: z.array(ActivityResponse),
    unitsOfMeasure: z.array(UnitOfMeasureResponse),
  })
  .strict();
const TenantResponseApiResult = z
  .object({
    hasSucceeded: z.boolean(),
    errors: z.array(Error),
    data: TenantResponse,
  })
  .strict();

export const schemas = {
  Errors,
  Error,
  ActionStatuses,
  ActionPriorities,
  CommentResponse,
  ActionResponse,
  ActionResponseIEnumerableApiResult,
  ApiResult,
  CreateActionRequest,
  ActionResponseApiResult,
  UpdateActionRequest,
  CreateCommentRequest,
  CommentResponseApiResult,
  ActivityTypeResponse,
  ProblemDetails,
  CreateActivityTypeRequest,
  ActivityResponse,
  ActivityResponseApiResult,
  UpdateActivityTypeRequest,
  ActivityTypeResponseApiResult,
  CreateActivityRequest,
  ConnectActivityRequest,
  BackfillFileImportSummariesRequest,
  BackfillFileImportSummariesResult,
  BackfillFileImportSummariesResultApiResult,
  ReprocessCombinedImportDataRequest,
  ReprocessCombinedImportDataResult,
  ReprocessCombinedImportDataResultApiResult,
  SynchronizeAggregatedInternalsRequest,
  SynchronizemovingTotalInternalsRequest,
  SynchronizeProducedActivitiesRequest,
  SkippedRecordsDetailsResponse,
  FileImportSummaryResponse,
  FileImportSummaryResponseApiResult,
  File_UploadSuggestionImage_Body,
  StringApiResult,
  File_UploadFile_Body,
  FileResponse,
  FileResponseApiResult,
  ImprovementAreaResponse,
  ImprovementAreaResponseIEnumerableApiResult,
  CreateImprovementAreaRequest,
  ImprovementAreaResponseApiResult,
  UpdateImprovementAreaRequest,
  FlexHRMEmployeesRequest,
  EmployeeId,
  ActorId,
  TenantId,
  BranchId,
  DistributionCenterId,
  EmployeeNumberId,
  EmployeeNumber,
  Employee,
  UpdateFlexEmployeesResponse,
  UpdateFlexEmployeesResponseApiResult,
  FlexHRMDateRequest,
  ActivityId,
  ProducedActivityResult,
  RefreshDateResponse,
  RefreshDateResponseApiResult,
  InternalResolution,
  AggregatedInternalBucketResolution,
  DateResolution,
  CalculatedInternalRequest,
  CalculatedInternalResponse,
  CalculatedInternalResponseIEnumerableApiResult,
  ProcessAggregatedInternalsRequest,
  AggregatedInternalId,
  INotification,
  ZoneId,
  SubZoneId,
  UnitOfMeasureId,
  AggregatedInternal,
  ProcessAggregatedInternalResult,
  ProcessAggregatedInternalResultApiResult,
  CreateAggregatedInternalRequest,
  IssueMobileTokenRequest,
  MobileTokenBrokerResponse,
  MobileTokenBrokerResponseApiResult,
  BaseRequest,
  PredictionResponse,
  PredictionResponseIEnumerableApiResult,
  CreatePredictionRequest,
  PredictionResponseApiResult,
  ProducedActivityRequest,
  ProducedActivityResponse,
  ProducedActivityResponseIEnumerableApiResult,
  AggregatedActivityResolution,
  AggregatedActivityRequest,
  AggregatedActivityResponse,
  AggregatedActivityResponseIEnumerableApiResult,
  SuggestionResponse,
  SuggestionResponseIEnumerableApiResult,
  CreateSuggestionRequest,
  SuggestionResponseApiResult,
  BoundaryResponse,
  BranchResponse,
  CountryResponse,
  CityResponse,
  AddressResponse,
  DistributionCenterResponse,
  AreaResponse,
  ProductivityGoalResponse,
  ProductivityNumberResponse,
  PercentOfTotalRowsResponse,
  ZoneResponse,
  SubZoneResponse,
  EmployeeNumberResponse,
  EmployeeResponse,
  UnitOfMeasureResponse,
  TenantResponse,
  TenantResponseApiResult,
};

/**
 * TYPESCRIPT TYPES
 */
export type Errors = z.infer<typeof Errors>;
export type Error = z.infer<typeof Error>;
export type ActionStatuses = z.infer<typeof ActionStatuses>;
export type ActionPriorities = z.infer<typeof ActionPriorities>;
export type CommentResponse = z.infer<typeof CommentResponse>;
export type ActionResponse = z.infer<typeof ActionResponse>;
export type ActionResponseIEnumerableApiResult = z.infer<
  typeof ActionResponseIEnumerableApiResult
>;
export type ApiResult = z.infer<typeof ApiResult>;
export type CreateActionRequest = z.infer<typeof CreateActionRequest>;
export type ActionResponseApiResult = z.infer<typeof ActionResponseApiResult>;
export type UpdateActionRequest = z.infer<typeof UpdateActionRequest>;
export type CreateCommentRequest = z.infer<typeof CreateCommentRequest>;
export type CommentResponseApiResult = z.infer<typeof CommentResponseApiResult>;
export type ActivityTypeResponse = z.infer<typeof ActivityTypeResponse>;
export type ProblemDetails = z.infer<typeof ProblemDetails>;
export type CreateActivityTypeRequest = z.infer<
  typeof CreateActivityTypeRequest
>;
export type ActivityResponse = z.infer<typeof ActivityResponse>;
export type ActivityResponseApiResult = z.infer<
  typeof ActivityResponseApiResult
>;
export type UpdateActivityTypeRequest = z.infer<
  typeof UpdateActivityTypeRequest
>;
export type ActivityTypeResponseApiResult = z.infer<
  typeof ActivityTypeResponseApiResult
>;
export type CreateActivityRequest = z.infer<typeof CreateActivityRequest>;
export type ConnectActivityRequest = z.infer<typeof ConnectActivityRequest>;
export type BackfillFileImportSummariesRequest = z.infer<
  typeof BackfillFileImportSummariesRequest
>;
export type BackfillFileImportSummariesResult = z.infer<
  typeof BackfillFileImportSummariesResult
>;
export type BackfillFileImportSummariesResultApiResult = z.infer<
  typeof BackfillFileImportSummariesResultApiResult
>;
export type ReprocessCombinedImportDataRequest = z.infer<
  typeof ReprocessCombinedImportDataRequest
>;
export type ReprocessCombinedImportDataResult = z.infer<
  typeof ReprocessCombinedImportDataResult
>;
export type ReprocessCombinedImportDataResultApiResult = z.infer<
  typeof ReprocessCombinedImportDataResultApiResult
>;
export type SynchronizeAggregatedInternalsRequest = z.infer<
  typeof SynchronizeAggregatedInternalsRequest
>;
export type SynchronizemovingTotalInternalsRequest = z.infer<
  typeof SynchronizemovingTotalInternalsRequest
>;
export type SynchronizeProducedActivitiesRequest = z.infer<
  typeof SynchronizeProducedActivitiesRequest
>;
export type SkippedRecordsDetailsResponse = z.infer<
  typeof SkippedRecordsDetailsResponse
>;
export type FileImportSummaryResponse = z.infer<
  typeof FileImportSummaryResponse
>;
export type FileImportSummaryResponseApiResult = z.infer<
  typeof FileImportSummaryResponseApiResult
>;
export type File_UploadSuggestionImage_Body = z.infer<
  typeof File_UploadSuggestionImage_Body
>;
export type StringApiResult = z.infer<typeof StringApiResult>;
export type File_UploadFile_Body = z.infer<typeof File_UploadFile_Body>;
export type FileResponse = z.infer<typeof FileResponse>;
export type FileResponseApiResult = z.infer<typeof FileResponseApiResult>;
export type ImprovementAreaResponse = z.infer<typeof ImprovementAreaResponse>;
export type ImprovementAreaResponseIEnumerableApiResult = z.infer<
  typeof ImprovementAreaResponseIEnumerableApiResult
>;
export type CreateImprovementAreaRequest = z.infer<
  typeof CreateImprovementAreaRequest
>;
export type ImprovementAreaResponseApiResult = z.infer<
  typeof ImprovementAreaResponseApiResult
>;
export type UpdateImprovementAreaRequest = z.infer<
  typeof UpdateImprovementAreaRequest
>;
export type FlexHRMEmployeesRequest = z.infer<typeof FlexHRMEmployeesRequest>;
export type EmployeeId = z.infer<typeof EmployeeId>;
export type ActorId = z.infer<typeof ActorId>;
export type TenantId = z.infer<typeof TenantId>;
export type BranchId = z.infer<typeof BranchId>;
export type DistributionCenterId = z.infer<typeof DistributionCenterId>;
export type EmployeeNumberId = z.infer<typeof EmployeeNumberId>;
export type EmployeeNumber = z.infer<typeof EmployeeNumber>;
export type Employee = z.infer<typeof Employee>;
export type UpdateFlexEmployeesResponse = z.infer<
  typeof UpdateFlexEmployeesResponse
>;
export type UpdateFlexEmployeesResponseApiResult = z.infer<
  typeof UpdateFlexEmployeesResponseApiResult
>;
export type FlexHRMDateRequest = z.infer<typeof FlexHRMDateRequest>;
export type ActivityId = z.infer<typeof ActivityId>;
export type ProducedActivityResult = z.infer<typeof ProducedActivityResult>;
export type RefreshDateResponse = z.infer<typeof RefreshDateResponse>;
export type RefreshDateResponseApiResult = z.infer<
  typeof RefreshDateResponseApiResult
>;
export type InternalResolution = z.infer<typeof InternalResolution>;
export type AggregatedInternalBucketResolution = z.infer<
  typeof AggregatedInternalBucketResolution
>;
export type DateResolution = z.infer<typeof DateResolution>;
export type CalculatedInternalRequest = z.infer<
  typeof CalculatedInternalRequest
>;
export type CalculatedInternalResponse = z.infer<
  typeof CalculatedInternalResponse
>;
export type CalculatedInternalResponseIEnumerableApiResult = z.infer<
  typeof CalculatedInternalResponseIEnumerableApiResult
>;
export type ProcessAggregatedInternalsRequest = z.infer<
  typeof ProcessAggregatedInternalsRequest
>;
export type AggregatedInternalId = z.infer<typeof AggregatedInternalId>;
export type INotification = z.infer<typeof INotification>;
export type ZoneId = z.infer<typeof ZoneId>;
export type SubZoneId = z.infer<typeof SubZoneId>;
export type UnitOfMeasureId = z.infer<typeof UnitOfMeasureId>;
export type AggregatedInternal = z.infer<typeof AggregatedInternal>;
export type ProcessAggregatedInternalResult = z.infer<
  typeof ProcessAggregatedInternalResult
>;
export type ProcessAggregatedInternalResultApiResult = z.infer<
  typeof ProcessAggregatedInternalResultApiResult
>;
export type CreateAggregatedInternalRequest = z.infer<
  typeof CreateAggregatedInternalRequest
>;
export type IssueMobileTokenRequest = z.infer<typeof IssueMobileTokenRequest>;
export type MobileTokenBrokerResponse = z.infer<
  typeof MobileTokenBrokerResponse
>;
export type MobileTokenBrokerResponseApiResult = z.infer<
  typeof MobileTokenBrokerResponseApiResult
>;
export type BaseRequest = z.infer<typeof BaseRequest>;
export type PredictionResponse = z.infer<typeof PredictionResponse>;
export type PredictionResponseIEnumerableApiResult = z.infer<
  typeof PredictionResponseIEnumerableApiResult
>;
export type CreatePredictionRequest = z.infer<typeof CreatePredictionRequest>;
export type PredictionResponseApiResult = z.infer<
  typeof PredictionResponseApiResult
>;
export type ProducedActivityRequest = z.infer<typeof ProducedActivityRequest>;
export type ProducedActivityResponse = z.infer<typeof ProducedActivityResponse>;
export type ProducedActivityResponseIEnumerableApiResult = z.infer<
  typeof ProducedActivityResponseIEnumerableApiResult
>;
export type AggregatedActivityResolution = z.infer<
  typeof AggregatedActivityResolution
>;
export type AggregatedActivityRequest = z.infer<
  typeof AggregatedActivityRequest
>;
export type AggregatedActivityResponse = z.infer<
  typeof AggregatedActivityResponse
>;
export type AggregatedActivityResponseIEnumerableApiResult = z.infer<
  typeof AggregatedActivityResponseIEnumerableApiResult
>;
export type SuggestionResponse = z.infer<typeof SuggestionResponse>;
export type SuggestionResponseIEnumerableApiResult = z.infer<
  typeof SuggestionResponseIEnumerableApiResult
>;
export type CreateSuggestionRequest = z.infer<typeof CreateSuggestionRequest>;
export type SuggestionResponseApiResult = z.infer<
  typeof SuggestionResponseApiResult
>;
export type BoundaryResponse = z.infer<typeof BoundaryResponse>;
export type BranchResponse = z.infer<typeof BranchResponse>;
export type CountryResponse = z.infer<typeof CountryResponse>;
export type CityResponse = z.infer<typeof CityResponse>;
export type AddressResponse = z.infer<typeof AddressResponse>;
export type DistributionCenterResponse = z.infer<
  typeof DistributionCenterResponse
>;
export type AreaResponse = z.infer<typeof AreaResponse>;
export type ProductivityGoalResponse = z.infer<typeof ProductivityGoalResponse>;
export type ProductivityNumberResponse = z.infer<
  typeof ProductivityNumberResponse
>;
export type PercentOfTotalRowsResponse = z.infer<
  typeof PercentOfTotalRowsResponse
>;
export type ZoneResponse = z.infer<typeof ZoneResponse>;
export type SubZoneResponse = z.infer<typeof SubZoneResponse>;
export type EmployeeNumberResponse = z.infer<typeof EmployeeNumberResponse>;
export type EmployeeResponse = z.infer<typeof EmployeeResponse>;
export type UnitOfMeasureResponse = z.infer<typeof UnitOfMeasureResponse>;
export type TenantResponse = z.infer<typeof TenantResponse>;
export type TenantResponseApiResult = z.infer<typeof TenantResponseApiResult>;

const endpoints = makeApi([
  {
    method: 'post',
    path: '/v1/mobile/auth/token',
    alias: 'MobileAuth_IssueToken',
    description: `Issues a short-lived mobile API token after attestation validation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IssueMobileTokenRequest,
      },
    ],
    response: MobileTokenBrokerResponseApiResult,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ApiResult,
      },
      {
        status: 500,
        description: `Internal Server Error`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId',
    alias: 'Tenant_Get',
    description: `Returns tenant details with hydrated related data.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: TenantResponseApiResult,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/activities',
    alias: 'Activity_CreateActivity',
    description: `Creates a new activity under an activity type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateActivityRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActivityResponseApiResult,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/activity-types',
    alias: 'Activity_CreateActivityType',
    description: `Creates a new activity type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateActivityTypeRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActivityResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/activity-types/:activityTypeId',
    alias: 'Activity_GetActivityType',
    description: `Returns one activity type for the tenant.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'activityTypeId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActivityTypeResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/activity-types/:activityTypeId/update',
    alias: 'Activity_UpdateActivityType',
    description: `Updates an existing activity type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateActivityTypeRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'activityTypeId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActivityTypeResponseApiResult,
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/files/:importStagingFileId/rebuild-import-summary',
    alias: 'Admin_RebuildImportSummary',
    description: `Recalculates and upserts the FileImportSummary row for the given ImportStagingFile. Safe to call multiple times.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'importStagingFileId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/files/backfill-import-summaries',
    alias: 'Admin_BackfillImportSummaries',
    description: `Rebuilds FileImportSummary rows for all ImportStagingFiles created within the given date range. Safe to re-run.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BackfillFileImportSummariesRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: BackfillFileImportSummariesResultApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/files/reprocess-combined-import-data',
    alias: 'Admin_ReprocessCombinedImportData',
    description: `Resets IsProcessed on staging data within the date range and re-triggers combined processing. Missing Internals and InternalRows will be created; existing ones are skipped.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReprocessCombinedImportDataRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ReprocessCombinedImportDataResultApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/synchronize-aggregated-internals',
    alias: 'Admin_SynchronizeAggregatedInternals',
    description: `Copies aggregated internals from source tenant into demo tenant.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SynchronizeAggregatedInternalsRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/synchronize-moving-total-internals',
    alias: 'Admin_SynchronizeMovingTotalInternals',
    description: `Copies moving total internals from source tenant into demo tenant.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SynchronizemovingTotalInternalsRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/administration/synchronize-produced-activities',
    alias: 'Admin_SynchronizeProducedActivities',
    description: `Copies produced activities from source tenant into demo tenant.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SynchronizeProducedActivitiesRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/aggregated-activities',
    alias: 'ProducedActivity_GetAggregatedActivities',
    description: `Returns aggregated activities based on resolution and date window.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AggregatedActivityRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: AggregatedActivityResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/aggregated-internals/create',
    alias: 'Internal_CreateAggregatedInternal',
    description: `Creates a new aggregated internal row.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAggregatedInternalRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/aggregated-internals/process',
    alias: 'Internal_ProcessAggreatedInternals',
    description: `Processes aggregated internals for the selected dates and resolution.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProcessAggregatedInternalsRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ProcessAggregatedInternalResultApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/calculated-internals',
    alias: 'Internal_GetCalculatedInternals',
    description: `Returns calculated internals or raw aggregated internals based on request flags.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CalculatedInternalRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: CalculatedInternalResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/connect-activities',
    alias: 'Activity_ConnectActivityToZoneAndSubZone',
    description: `Creates a connection between an activity and a zone/subzone.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConnectActivityRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/files',
    alias: 'File_GetFiles',
    description: `Returns paged integration/import files with status information.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'PageNumber',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'PageSize',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: FileImportSummaryResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/flexhrm/refresh-date',
    alias: 'Integration_UpdateDayTimes',
    description: `Pulls FlexHRM day-level data for the selected date.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FlexHRMDateRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: RefreshDateResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/flexhrm/refresh-employees',
    alias: 'Integration_UpdateEmployees',
    description: `Pulls employee data from FlexHRM for the selected branch and distribution center.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FlexHRMEmployeesRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: UpdateFlexEmployeesResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/improvements/actions',
    alias: 'Action_GetActions',
    description: `Returns all improvement actions connected to the tenant.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActionResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/actions',
    alias: 'Action_Create',
    description: `Creates a new improvement action.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The request object containing all necessary details for the action. This includes the title, problem statement,
solution proposal, definition of done, status, start date, end date, the responsible employee&#x27;s identifier,
and any associated suggestion identifiers.`,
        type: 'Body',
        schema: CreateActionRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActionResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/tenants/:tenantId/improvements/actions/:actionId',
    alias: 'Action_Delete',
    description: `Deletes an improvement action and connected comments/suggestion links.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'actionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/actions/:actionId/comments',
    alias: 'Action_Comment',
    description: `Adds a new comment to an action.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `CreateComment request body`,
        type: 'Body',
        schema: z.object({ text: z.string() }).strict(),
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'actionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: CommentResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/actions/:actionId/update',
    alias: 'Action_Update',
    description: `Updates an existing improvement action.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The update Action object`,
        type: 'Body',
        schema: UpdateActionRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'actionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ActionResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/improvements/improvement-areas',
    alias: 'ImprovementArea_GetAll',
    description: `Returns all improvement areas within the tenant scope.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ImprovementAreaResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/improvement-areas',
    alias: 'ImprovementArea_Create',
    description: `Name must be unique in the selected scope (tenant global, branch, or branch+distribution center).
Sort order is validated and normalized by business rules.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Create payload.`,
        type: 'Body',
        schema: CreateImprovementAreaRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ImprovementAreaResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/improvement-areas/:improvementAreaId/soft-delete',
    alias: 'ImprovementArea_SoftDelete',
    description: `Soft delete is blocked when actions or suggestions are connected to the improvement area.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'improvementAreaId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/improvement-areas/:improvementAreaId/update',
    alias: 'ImprovementArea_Update',
    description: `Supports updating scope, name, description and sort order.
Affected sibling sort orders are rebalanced as needed.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Update payload.`,
        type: 'Body',
        schema: UpdateImprovementAreaRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'improvementAreaId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ImprovementAreaResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/improvements/suggestions',
    alias: 'Suggestion_GetSuggestions',
    description: `Returns all improvement suggestions in the tenant scope.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: SuggestionResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/suggestions',
    alias: 'Suggestion_CreateSuggestion',
    description: `Creates a new improvement suggestion.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Request object`,
        type: 'Body',
        schema: CreateSuggestionRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: SuggestionResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tenants/:tenantId/improvements/suggestions/:suggestionId',
    alias: 'Suggestion_GetSuggestion',
    description: `Returns a single suggestion including hydrated file references.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'suggestionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: SuggestionResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/tenants/:tenantId/improvements/suggestions/:suggestionId',
    alias: 'Suggestion_Delete',
    description: `Deletes a suggestion and related file/action-suggestion links.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'suggestionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/suggestions/:suggestionId/reject',
    alias: 'Suggestion_Reject',
    description: `Changes suggestion state to Rejected.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'suggestionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: ApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/improvements/suggestions/:suggestionId/upload-image',
    alias: 'File_UploadSuggestionImage',
    description: `Uploads an image and links it to a suggestion.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: File_UploadSuggestionImage_Body,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'suggestionId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: StringApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/predictions',
    alias: 'Prediction_Create',
    description: `Creates a new prediction row.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePredictionRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: PredictionResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/predictions/query',
    alias: 'Prediction_Search',
    description: `Returns predictions for the requested tenant scope and dates.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BaseRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: PredictionResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/produced-activities',
    alias: 'ProducedActivity_GetProducedActivities',
    description: `Returns produced activities for the given filters and paging.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProducedActivityRequest,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'PageNumber',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'PageSize',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: ProducedActivityResponseIEnumerableApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z
          .object({
            type: z.string().nullable(),
            title: z.string().nullable(),
            status: z.number().int().nullable(),
            detail: z.string().nullable(),
            instance: z.string().nullable(),
          })
          .strict()
          .passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/tenants/:tenantId/upload',
    alias: 'File_UploadFile',
    description: `Uploads an integration file and stores metadata in the database.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: File_UploadFile_Body,
      },
      {
        name: 'tenantId',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: FileResponseApiResult,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: ApiResult,
      },
    ],
  },
]);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
